import { supabase, isDemoMode } from '@/lib/supabase';
import type { Database } from '@/types/database';

type WaitlistSignup = Database['public']['Tables']['waitlist_signups']['Row'];
type WaitlistSignupInsert = Database['public']['Tables']['waitlist_signups']['Insert'];

export interface WaitlistSignupData {
  email: string;
  source?: string;
  interests?: string[];
}

/**
 * Sign up for the waitlist
 * @param data - Email and optional metadata
 * @returns The created waitlist signup or null if in demo mode
 */
export async function signUpForWaitlist(data: WaitlistSignupData): Promise<WaitlistSignup | null> {
  // In demo mode, just simulate success
  if (isDemoMode()) {
    console.log('[Demo Mode] Waitlist signup:', data.email);
    return {
      id: crypto.randomUUID(),
      email: data.email,
      source: data.source || 'landing_page',
      interests: data.interests || [],
      referrer: null,
      ip_address: null,
      user_agent: null,
      status: 'active',
      converted_user_id: null,
      converted_at: null,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    };
  }

  const insertData: WaitlistSignupInsert = {
    email: data.email.toLowerCase().trim(),
    source: data.source || 'landing_page',
    interests: data.interests || ['launch_notification', 'feature_updates'],
    referrer: document.referrer || null,
    user_agent: navigator.userAgent || null,
  };

  const { data: signup, error } = await supabase
    .from('waitlist_signups')
    .insert(insertData)
    .select()
    .single();

  if (error) {
    // Check if it's a duplicate email error
    if (error.code === '23505') {
      throw new Error('This email is already on the waitlist!');
    }
    throw error;
  }

  return signup;
}

/**
 * Check if an email is already on the waitlist
 * @param email - Email to check
 * @returns True if already signed up
 */
export async function isEmailOnWaitlist(email: string): Promise<boolean> {
  if (isDemoMode()) {
    return false;
  }

  const { data, error } = await supabase
    .from('waitlist_signups')
    .select('id')
    .eq('email', email.toLowerCase().trim())
    .maybeSingle();

  if (error) {
    console.error('Error checking waitlist:', error);
    return false;
  }

  return !!data;
}

/**
 * Unsubscribe an email from the waitlist
 * @param email - Email to unsubscribe
 */
export async function unsubscribeFromWaitlist(email: string): Promise<void> {
  if (isDemoMode()) {
    console.log('[Demo Mode] Unsubscribe:', email);
    return;
  }

  const { error } = await supabase
    .from('waitlist_signups')
    .update({ status: 'unsubscribed' })
    .eq('email', email.toLowerCase().trim());

  if (error) {
    throw error;
  }
}
